import { action, computed, makeObservable, observable } from 'mobx';
import {
  AiDisqualificationBucket,
  aiDisqualificationBucket,
  aiDisqualificationBuckets,
  EvaluationComparison,
  HumanScrubBand,
  humanScrubBand,
  humanScrubBands,
  ScorePair,
  scorePairs,
  spearmanRankCorrelation,
  WinnerScoredLowerRow,
  winnersScoredLower,
} from '../../models';

type MatrixRow = Record<AiDisqualificationBucket, number>;

export class EvaluationComparisonUiStore {
  @observable rows: EvaluationComparison[] = [];

  @observable isLoading = false;

  constructor() {
    makeObservable(this);
  }

  @action
  setRows(rows: EvaluationComparison[]): void {
    this.rows = rows;
  }

  @action
  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  @action
  reset(): void {
    this.rows = [];
    this.isLoading = false;
  }

  @computed
  get matrix(): Record<HumanScrubBand, MatrixRow> {
    const emptyRow = (): MatrixRow => ({
      'ai-rule-disqualified': 0,
      'ai-llm-disqualified': 0,
      'ai-passed': 0,
      'ai-did-not-complete': 0,
    });
    const matrix: Record<HumanScrubBand, MatrixRow> = {
      'human-disqualified': emptyRow(),
      'human-passed': emptyRow(),
      'no-human-label': emptyRow(),
    };
    this.rows.forEach((row) => {
      matrix[humanScrubBand(row)][aiDisqualificationBucket(row)] += 1;
    });
    return matrix;
  }

  @computed
  get bandTotals(): Record<HumanScrubBand, number> {
    const matrix = this.matrix;
    return humanScrubBands.reduce((totals, band) => {
      totals[band] = aiDisqualificationBuckets.reduce((sum, bucket) => sum + matrix[band][bucket], 0);
      return totals;
    }, {} as Record<HumanScrubBand, number>);
  }

  @computed
  get columnTotals(): Record<AiDisqualificationBucket, number> {
    const matrix = this.matrix;
    return aiDisqualificationBuckets.reduce((totals, bucket) => {
      totals[bucket] = humanScrubBands.reduce((sum, band) => sum + matrix[band][bucket], 0);
      return totals;
    }, {} as Record<AiDisqualificationBucket, number>);
  }

  @computed
  get grandTotal(): number {
    return this.rows.length;
  }

  @computed
  get sortedScorePairs(): ScorePair[] {
    return scorePairs(this.rows).sort((a, b) => b.humanAggregateScore - a.humanAggregateScore);
  }

  @computed
  get scoreAgreement(): number | null {
    return spearmanRankCorrelation(this.sortedScorePairs);
  }

  @computed
  get winnersScoredLower(): WinnerScoredLowerRow[] {
    return winnersScoredLower(this.rows);
  }

  @computed
  get aiLlmDisqualifications(): EvaluationComparison[] {
    return this.rows.filter((row) => aiDisqualificationBucket(row) === 'ai-llm-disqualified');
  }
}
